<template>
    <div class="flex flex-col px-10 py-10 mb-10 -mx-10 -mt-10 text-sm text-green-700 bg-green-100 sm:flex-row sm:items-center">
        <div class="flex items-center mb-3 mr-3 font-bold sm:mb-0"><svg class="w-4 h-4 mr-3 text-green-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path></svg> Upload Later</div>
        <div class="flex-1 font-medium">We will send you an email for the verification options.</div>
    </div>

    <div>
        <div class="pb-10 mb-12 border-b border-gray-200">
            <h2 class="pb-1 text-2xl font-bold text-left text-black no-underline normal-case non-italic">Investor Verification</h2>
            <p class="text-lg font-normal text-left no-underline normal-case non-italic">Please indicate the amount that you would like to invest.</p>
        </div>

        <app-errors :form="form" class="mb-10"></app-errors>

        <div class="space-y-8 text-base font-medium prose text-gray-600 max-w-none">
            <p>Thank you for your subscription in <strong>Mammoth Technology, LLC</strong>. Due to securities regulations, we are required to verify your accredited investor status. Please upload any of the following documents to complete the verification:</p>

            <div class="-space-y-px bg-white rounded-md">
                <label class="relative flex p-4 border border-gray-200 cursor-pointer rounded-tl-md rounded-tr-md" :class="{
                    'bg-teal bg-opacity-5 border-teal z-10': form.documentsType == 'Assets'
                }">
                    <input type="radio" v-model="form.documentsType" value="Assets" class="h-4 w-4 mt-0.5 cursor-pointer text-teal border-gray-300 focus:ring-teal">
                    <div class="flex flex-col ml-3 -mt-1">
                        <span class="block text-lg font-medium text-gray-900">
                            Assets
                        </span>
                        <span class="block text-sm text-gray-500">
                            Account statements or third party appraisals dated within the past 90 days proving at least $1M of assets outside of your primary residence. You do not need to prove more than $1M in assets.
                        </span>
                    </div>
                </label>

                <label class="relative flex p-4 border border-gray-200 cursor-pointer" :class="{
                    'bg-teal bg-opacity-5 border-teal z-10': form.documentsType == 'Professional Letter'
                }">
                    <input type="radio" v-model="form.documentsType" value="Professional Letter" class="h-4 w-4 mt-0.5 cursor-pointer text-teal border-gray-300 focus:ring-teal">
                    <div class="flex flex-col ml-3 -mt-1">
                        <span class="block text-lg font-medium text-gray-900">
                            Professional Letter or Professional License
                        </span>
                        <span class="block text-sm text-gray-500">
                            Official written communication dated within the past 90 days from a licensed CPA, attorney, investment advisor or registered broker-dealer that you are an Accredited Investor. If you are licensed as an investment professional, please submit a Broker Check Detailed Report or SEC Investment Advisor Detailed Report. <a :href="asset('/files/ACCREDITED-LETTER-EXAMPLE.pdf')" class="!text-black !font-bold !no-underline" target="_blank">See example here</a>.
                        </span>
                    </div>
                </label>

                <label class="relative flex p-4 border border-gray-200 cursor-pointer rounded-bl-md rounded-br-md" :class="{
                    'bg-teal bg-opacity-5 border-teal z-10': form.documentsType == 'Income'
                }">
                    <input type="radio" v-model="form.documentsType" value="Income" class="h-4 w-4 mt-0.5 cursor-pointer text-teal border-gray-300 focus:ring-teal">
                    <div class="flex flex-col ml-3 -mt-1">
                        <span class="block text-lg font-medium text-gray-900">
                            Income
                        </span>
                        <span class="block text-sm text-gray-500">
                            Tax returns including W2s, 1099s, K-1s for the past two years and documentation (W2 or financial statement) verifying your year to date income, to show your individual income exceeds $200,000 (or joint income exceeds $300,000) for each year. Note: If your tax return for the previous year is not yet complete, please send your W2s, 1099s and a financial statement for that year.
                        </span>
                    </div>
                </label>
            </div>
        </div>

        <div class="flex items-center justify-center mb-8 mt-14">
            <div class="block w-full max-w-lg">
                <label class="block mb-1 text-sm font-medium text-gray-700">Choose a file</label>
                <uploads-field v-model="form.documents" accept=".pdf" desc="Accepts PDF files up to 10MB"></uploads-field>
            </div>
        </div>

        <div class="flex items-start justify-between mt-20 sm:items-center">
            <app-button @click="handleBack" class="!border-gray-200 !bg-white !text-gray-400">Back</app-button>

            <div class="flex flex-col items-center sm:flex-row-reverse sm:space-y-0">
                <app-button @click="handleNext" :busy="form.busy" class="w-[178px] mb-3 sm:ml-3 sm:mb-0">Save and Continue</app-button>

                <app-button @click="handleNotify" :busy="form.busy" class="w-[178px] md:w-[135px] !bg-white !text-black !border !border-gray-300">Upload Later</app-button>
            </div>
        </div>
    </div>
</template>

<script>
import AppButton from '@Components/Button'
import AppErrors from '@Components/Errors'
import UploadsField from '@Fields/Uploads'
import CheckboxField from '@Fields/Checkbox'
import useForm from '@/form'
import { useStore } from 'vuex'

export default {
    components: {
        AppButton,
        AppErrors,
        UploadsField,
        CheckboxField
    },
    props: ['series'],
    emits: ['prev', 'next', 'process', 'complete', 'goto'],
    setup(props, { emit }){
        const store = useStore()

        const form = useForm(_.assign({
            documentsType: '',
            documents: [],
            uploadLater: false
        }, store.state.verificationForm));

        function handleBack(){
            emit('prev', true)
        }

        function handleNotify(){
            form.uploadLater = true
            saveForm()
            emit('next', true)
        }

        function handleNext(){
            form.uploadLater = false

            form.post(route('intake.validate.verification')).then(response => {
                saveForm()

                emit('next', true)
            })
        }

        function saveForm(){
            store.commit('UPDATE_FORM_DATA', {
                form: 'verificationForm',
                data: form.data()
            });
        }

        return {
            form,
            handleBack,
            handleNotify,
            handleNext
        }
    },
}
</script>

<style scoped>
.prose ul > li::before {
    background-color: #00f4c8;
}
</style>
